import { useState } from 'react'
import type { Household, Member } from '../../lib/supabase'
import { MAX_MEMBERS } from '../../lib/pricing'
import { initialsOf, memberTint } from '../../lib/initials'
import { Section } from './Section'
import { focusOnMount } from '../../hooks/useAutoFocus'

// The household (spec 06 §1): its name, the invite code to share, and who is
// on it. Up to six people (031); removing someone frees their place and, since
// they still know the code, offers a fresh one. Leaving is your own row.
export function HouseholdSection({ household, members, self, onRename, onRemoveMember, onResetInviteCode, onLeft }: {
  household: Household
  members: Member[]
  self: Member | null
  onRename: (name: string) => void
  onRemoveMember: (memberId: string) => Promise<string | null>
  onResetInviteCode: () => Promise<string | null>
  /** You left: the household is gone from under the panel. */
  onLeft: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState('')
  const [copied, setCopied] = useState(false)
  const [confirming, setConfirming] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [offerReset, setOfferReset] = useState(false)
  const [codeReset, setCodeReset] = useState(false)
  const full = members.length >= MAX_MEMBERS

  // You first, then the household's order.
  const ordered = self ? [self, ...members.filter(m => m.id !== self.id)] : members
  const tintIndex = new Map(members.map((m, i) => [m.id, i]))

  function commit() {
    if (name.trim() && name.trim() !== household.name) onRename(name.trim())
    setEditing(false)
  }

  async function copy() {
    try { await navigator.clipboard?.writeText(household.invite_code) } catch { /* shown regardless: the code is on screen */ }
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  async function remove(m: Member) {
    setBusy(m.id)
    setError(null)
    const err = await onRemoveMember(m.id)
    setBusy(null)
    setConfirming(null)
    if (err) { setError(err); return }
    if (m.id === self?.id) { onLeft(); return }
    setOfferReset(true)
  }

  async function resetCode() {
    setError(null)
    const err = await onResetInviteCode()
    if (err) { setError(err); return }
    setOfferReset(false)
    setCodeReset(true)
  }

  return (
    <Section title="Household">
      <div className="ml-settings__name">
        {editing ? (
          <input className="ml-input" aria-label="Household name" ref={focusOnMount} value={name}
            onChange={e => setName(e.target.value)} onBlur={commit}
            onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false) }} />
        ) : (
          <button type="button" className="ml-settings__name-btn" aria-label={`Rename ${household.name}`}
            onClick={() => { setName(household.name); setEditing(true) }}>
            {household.name}
          </button>
        )}
      </div>

      <div className="ml-invite-card">
        <div className="ml-invite-card__text">
          <span className="ml-invite-card__for">Invite code for {household.name}</span>
          <span className="ml-invite-card__code ml-num">{household.invite_code}</span>
          <span className="ml-invite-card__help">
            {full
              ? `The household is full (${MAX_MEMBERS} people). Someone has to leave before anyone else can join.`
              : 'Share it to add someone.'}
          </span>
        </div>
        <button type="button" className="ml-btn ml-btn--primary" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
      </div>
      {codeReset && <p className="ml-settings__note" role="status">The invite code is new. The old one no longer works.</p>}

      <ul className="ml-member-list" aria-label="Members">
        {ordered.map(m => {
          const isSelf = m.id === self?.id
          const verb = isSelf ? 'Leave' : 'Remove'
          const target = isSelf ? household.name : m.display_name
          return (
            <li key={m.id} className="ml-member">
              <span className="ml-avatar" style={{ background: memberTint(tintIndex.get(m.id) ?? 0) }} aria-hidden="true">
                {initialsOf(m.display_name)}
              </span>
              <span className="ml-member__text">
                <span className="ml-member__name">{m.display_name}</span>
                <span className="ml-member__role">{isSelf ? 'You' : 'Member'}</span>
              </span>
              {confirming === m.id ? (
                <span className="ml-member__confirm">
                  <button type="button" className="ml-text-btn ml-text-btn--destructive" disabled={busy === m.id}
                    aria-label={`Confirm ${verb.toLowerCase()} ${target}`} onClick={() => remove(m)}>{verb}</button>
                  <button type="button" className="ml-text-btn ml-text-btn--quiet" onClick={() => setConfirming(null)}>Cancel</button>
                </span>
              ) : (
                <button type="button" className="ml-text-btn ml-text-btn--quiet" aria-label={`${verb} ${target}`}
                  onClick={() => { setConfirming(m.id); setError(null) }}>{verb}</button>
              )}
            </li>
          )
        })}
      </ul>

      {offerReset && (
        <div className="ml-settings__offer">
          <p>They could rejoin with the invite code they have. Give the household a new one?</p>
          <button type="button" className="ml-text-btn" onClick={resetCode}>New invite code</button>
        </div>
      )}
      {error && <p className="ml-settings__error" role="alert">{error}</p>}
    </Section>
  )
}
